import { useState } from 'react'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { Checkbox } from '../../platform/primitives/Checkbox'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { shortDate } from '../transactions/txnDisplay'
import { applyFailed, appliedMessage, count } from './applyCopy'
import { AmountCell } from './ruleRowCells'
import type { ReadRule } from './ruleWire'
import { useApplyRule } from './useRuleApply'
import { canTick, MATCH_STATUS_LABEL, useRuleMatches, type RuleMatch } from './useRuleMatches'
import { RulesError } from './useRules'
import { useRuleRowTxns } from './useRuleRowTxns'

export const MATCHES_EMPTY = "This rule doesn't match any transactions right now."
const STILL_LOOKING = 'None found yet. Show more to keep looking.'
export const PAUSED_NOTE = 'This rule is paused. Turn it on to apply it.'

const LABEL = 'Transactions this rule matches'

function StatusChip({ m }: { m: RuleMatch }) {
  return <span className={`rule-match__status rule-match__status--${m.status}`}>{MATCH_STATUS_LABEL[m.status]}</span>
}

function MerchantCell({ m }: { m: RuleMatch }) {
  const name = m.merchantText ?? 'Unknown'
  return (
    <span className="rule-applied__merchant">
      <MerchantAvatar merchant={{ id: m.transactionId, name, type: 'other', is_global: false }} />
      <InlineLink to={`/transactions/${encodeURIComponent(m.transactionId)}`} className="rule-applied__link">
        <span className="rule-applied__name">{name}</span>
      </InlineLink>
    </span>
  )
}

/** "Select Bean Hut, Sep 30" — each box names its own row. */
const tickLabel = (m: RuleMatch) => `Select ${m.merchantText ?? 'Unknown'}, ${shortDate(m.date)}`

/**
 * "Matches now": every transaction the rule's conditions match today, each marked with what applying
 * would do. Rows that would change, or that hold a value the user set, can be ticked and applied as
 * one batch; "Already right", "Removed" and the rest cannot, and carry no box at all rather than a
 * disabled one.
 *
 * Ticking a "You set this" row is asking to change a value the user set: the apply sends
 * `overwrite_existing: true` and `respect_user_edits: false`, hand edits included (owner ruling
 * 2026-10-15), and the footer says so. With none ticked, a field edited by hand is left alone, and
 * the result says how many.
 *
 * A paused rule cannot be applied (the server refuses it): no boxes, and a line saying why. Its Turn on
 * is the page's, in the tools row above.
 */
export function RuleMatches({
  read,
  onUndo,
}: {
  read: ReadRule
  /** Raise the page's Undo confirmation for the batch an apply wrote. */
  onUndo: (batchId: string) => void
}) {
  const ruleId = read.rule.id
  const enabled = read.rule.is_enabled
  const { state, hasMore, loadingMore, loadMore } = useRuleMatches(ruleId)
  const matches = state.status === 'success' ? state.data.matches : []
  // Only the currency is read from the transaction: the match carries its own amount.
  const lookup = useRuleRowTxns(matches.map((m) => m.transactionId))
  const phone = useBreakpoint() === 'phone'
  const toast = useToast()
  const apply = useApplyRule(ruleId)
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())

  // Only rows still on screen and still tickable count: a refetch can move a row to "Already right".
  const chosen = matches.filter((m) => ticked.has(m.transactionId) && canTick(m.status))
  const youSet = chosen.filter((m) => m.status === 'you_set').length
  const toggle = (id: string) =>
    setTicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const applySelected = () => {
    if (apply.isPending || chosen.length === 0) return
    apply.mutate(
      { kind: 'selected', transactionIds: chosen.map((m) => m.transactionId), overwrite: youSet > 0 },
      {
        onSuccess: (result) => {
          setTicked(new Set())
          toast.show({
            kind: 'success',
            message: appliedMessage(read.rule.name, result),
            ...(result.modified > 0 ? { action: { label: 'Undo', onAct: () => onUndo(result.batchId) } } : {}),
          })
        },
        onError: (e) => toast.show({ kind: 'error', message: applyFailed(e instanceof RulesError ? e.status : undefined) }),
      },
    )
  }

  const box = (m: RuleMatch) =>
    enabled && canTick(m.status) ? (
      <Checkbox checked={ticked.has(m.transactionId)} onChange={() => toggle(m.transactionId)} label={tickLabel(m)} />
    ) : null

  const columns: Column<RuleMatch>[] = [
    ...(enabled ? [{ key: 'tick', header: 'Select', width: '44px', render: box }] : []),
    { key: 'date', header: 'Date', width: 'minmax(0, 0.7fr)', render: (m) => <span className="rule-applied__muted">{shortDate(m.date)}</span> },
    { key: 'merchant', header: 'Merchant', width: 'minmax(0, 1.6fr)', render: (m) => <MerchantCell m={m} /> },
    { key: 'status', header: 'Status', width: 'minmax(0, 1fr)', render: (m) => <StatusChip m={m} /> },
    { key: 'amount', header: 'Amount', width: 'minmax(0, 0.9fr)', align: 'end', render: (m) => <AmountCell t={lookup(m.transactionId)} cents={m.amountCents} /> },
  ]

  return (
    <Async
      surface="rule-matches"
      state={state}
      skeleton={
        <>
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </>
      }
      empty={{ title: MATCHES_EMPTY }}
    >
      {({ matches: rows }) => (
        <>
          {/* Text only: Turn on is in the page's tools row, one per screen (rv-architect M6b). */}
          {!enabled && <p className="rule-match__note rule-match__paused">{PAUSED_NOTE}</p>}
          {phone ? (
            <ul className="rule-applied__cards" aria-label={LABEL}>
              {rows.map((m) => (
                <li key={m.transactionId} className="rule-applied__card rule-match__card">
                  {enabled && <span className="rule-match__card-tick">{box(m)}</span>}
                  <div className="rule-match__card-body">
                    <div className="rule-applied__card-top">
                      <MerchantCell m={m} />
                      <AmountCell t={lookup(m.transactionId)} cents={m.amountCents} />
                    </div>
                    <div className="rule-match__card-meta">
                      <span className="rule-applied__muted">{shortDate(m.date)}</span>
                      <StatusChip m={m} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <DataGrid
              columns={columns}
              groups={[{ label: '', rows }]}
              rowKey={(m) => m.transactionId}
              label={LABEL}
              // A page the server's scan budget cut short is not "matches nothing".
              empty={hasMore ? STILL_LOOKING : MATCHES_EMPTY}
            />
          )}
          {hasMore && (
            <div className="rule-applied__more">
              <Button variant="outline" size="panel" aria-disabled={loadingMore || undefined} onClick={loadingMore ? undefined : loadMore}>
                {loadingMore ? 'Loading…' : 'Show more'}
              </Button>
            </div>
          )}
          {enabled && (
            <div className="rule-match__footer">
              {/* Mounted from the start, so the first tick is announced. */}
              <p className="rule-match__count" role="status">
                {chosen.length > 0 ? `${count(chosen.length)} selected` : ''}
                {youSet > 0 && (
                  <span className="rule-match__overwrite">{` · Includes ${count(youSet)} you set yourself`}</span>
                )}
              </p>
              {chosen.length > 0 && (
                <Button
                  variant="outline"
                  size="panel"
                  aria-disabled={apply.isPending || undefined}
                  aria-busy={apply.isPending || undefined}
                  onClick={applySelected}
                >
                  {apply.isPending ? 'Applying…' : 'Apply to selected'}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Async>
  )
}
